import {create} from 'zustand';
import {persist} from 'zustand/middleware';
import {User, type ProviderData} from '../models/User';
import type {LoginCredentials} from '../repository/AuthRepository';
import {authFactory} from './authFactory';
import {TokenStore} from '../../../Services/security/TokenStore';
import {tryRestoreSession} from '../../../Data/config/Http';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string; message?: string; user?: User }>;
  logout: () => Promise<void>;
  initializeAuth: () => Promise<void>;
  updateUserProviderData: (providerData: ProviderData | undefined) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => {
      const {loginUseCase, authRepository} = authFactory.getUseCases();

      return {
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
        initialized: false,

        login: async (credentials: LoginCredentials) => {
          set({loading: true, error: null});
          try {
            const result = await loginUseCase.execute(credentials);
            if (result.success && result.user) {
              set({isAuthenticated: true, user: result.user, loading: false});
            } else {
              set({isAuthenticated: false, user: null, loading: false, error: result.error || result.message || 'فشل تسجيل الدخول'});
            }
            return result;
          } catch (error) {
            const message = error instanceof Error ? error.message : 'حدث خطأ أثناء تسجيل الدخول';
            set({isAuthenticated: false, user: null, loading: false, error: message});
            return {success: false, error: message};
          }
        },

        logout: async () => {
          set({loading: true, error: null});
          try {
            await authRepository.logout();
          } catch (error) {
            const message = error instanceof Error ? error.message : 'حدث خطأ أثناء تسجيل الخروج';
            set({error: message});
          } finally {
            TokenStore.clear();
            set({isAuthenticated: false, user: null, loading: false, initialized: true});
          }
        },

        initializeAuth: async () => {
          const currentState = useAuthStore.getState();
          if (currentState.initialized || currentState.loading) return;

          const hasAccess = !!TokenStore.getAccessToken();
          const hasRefresh = !!TokenStore.getRefreshToken();
          if (!hasAccess && !hasRefresh) {
            set({initialized: true, loading: false, isAuthenticated: false, user: null});
            return;
          }

          set({loading: true});
          try {
            if (!hasAccess && hasRefresh) {
              const restored = await tryRestoreSession();
              if (!restored) {
                set({isAuthenticated: false, user: null, loading: false, initialized: true});
                return;
              }
            }
            const currentUser = await authRepository.getCurrentUser();
            const stateBeforeUpdate = useAuthStore.getState();
            if (stateBeforeUpdate.initialized) return;
            set({isAuthenticated: true, user: currentUser, loading: false, initialized: true});
          } catch {
            TokenStore.clear();
            set({isAuthenticated: false, user: null, loading: false, initialized: true});
          }
        },

        updateUserProviderData: (providerData: ProviderData | undefined) => {
          set((state) => {
            if (!state.user) {
              return state;
            }

            const current = state.user;
            const updatedUser = new User(
              current.id,
              current.username,
              current.email,
              current.role,
              current.status,
              current.permissions || [],
              providerData
            );

            return {
              ...state,
              user: updatedUser
            };
          });
        }
      };
    },
    {
      name: 'auth-store',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user
        // `initialized` is intentionally NOT persisted: on every page load it must start
        // as false so initializeAuth() runs and restores the in-memory access token from
        // the sessionStorage refresh token. Persisting it as true causes 401s on reload
        // because TokenStore.accessToken is memory-only and is lost between page loads.
      }),
      // Rehydrate User class instance from plain object
      merge: (persistedState: any, currentState: AuthState) => {
        // Rehydrate user from persisted state if it exists and is not a User instance
        if (persistedState?.user && !(persistedState.user instanceof User)) {
          const userData = persistedState.user as any;
          persistedState.user = new User(
            userData.id,
            userData.username,
            userData.email,
            userData.role,
            userData.status,
            userData.permissions || [],
            userData.providerData as ProviderData | undefined
          );
        }
        return {
          ...currentState,
          ...persistedState
        };
      }
    }
  )
);
